/* ============================================================
 * lbf-bundle-builder.js  (v3 — NATIVE card-product + native quick-view)
 * Travel Box Builder front-end logic.
 * Self-contained / upgrade-safe. Dishes are rendered server-side with the
 * native Dawn `card-product` snippet; this script reads each dish off the DOM
 * (data-variant-id / data-price / data-title / data-img on each .bb-dish),
 * layers the selection ring + "+ Add to box" stepper on top, neutralises the
 * card's own link nav + native add button, and wires the eye icon to the
 * card's native quick-view (modal-opener -> quick-add-modal).
 *
 * LOCKED MODEL (simplified):
 *   - Mix & match box. NO free gift. NO discount. Full retail price.
 *   - MOQ dishes to enable checkout (default 6).
 *   - Free shipping is the ONLY incentive: cart subtotal >= free_ship
 *     (West Malaysia). Threshold reuses window.LBF_SHIPPING.TDRY (cents)
 *     when present, else section setting (default RM150).
 *   - Static reminder header (NOT a progress bar).
 *   - Compact collapsed bottom bar with expandable "Your items" panel.
 *   - Eye icon opens the NATIVE quick-view popup (view details only).
 *   - Adding to the BOX is driven by the stepper, not the native add button.
 *   - Final add: separate /cart/add.js line items with bundle props.
 *
 * Gift code paths are gated behind cfg.enable_gifts (default false) and
 * are NOT exercised by default — nothing gift-related shows.
 * v4 (2026-08-04): bundle-funnel analytics + order attribution.
 * v5 (2026-08-04): dynamic gift eligibility from existing promo badges.
 * ============================================================ */
(function () {
  'use strict';

  var quickViewCache = window.__lbfBBQuickViewCache = window.__lbfBBQuickViewCache || {};

  function initRoot(root) {
    if (root._lbfBBInit) return;
    root._lbfBBInit = true;

    var dataId = root.getAttribute('data-bb-id');
    var cfg = window['LBF_BB_DATA_' + dataId] || {};

    /* ---- thresholds: prefer shared shipping core, fall back to section cfg ---- */
    var SHIP = window.LBF_SHIPPING || null;
    var MOQ = Number(cfg.moq) || 6;
    // free-shipping subtotal in RM. SHIP.TDRY is cents.
    var SHIP_FREE = (SHIP && SHIP.TDRY != null) ? (SHIP.TDRY / 100) : (Number(cfg.free_ship_value) || 150);
    var EDIT_BUNDLE_ID = getUrlParam('bb_edit');
    var SESSION_ID = EDIT_BUNDLE_ID || ('bb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8));
    var BUNDLE_NAME = cfg.bundle_name || 'Retort Travel Box';
    var SOURCE_PAGE = cfg.source_page || cleanSourcePage();
    /* ?from=soup-set: arrival from the homepage soup card's "I'll pick my own instead".
       Pool-tagged soups (tag "Any N Free ...") lead the grid and the counts read against
       that rule ("2 of 6 soups"). Everything else on the page is unchanged. */
    var SOUP_SET = getUrlParam('from') === 'soup-set';

    /* ---------- analytics: behavioral funnel (no customer data) ---------- */
    var analytics = window.LBF_BUNDLE_ANALYTICS = window.LBF_BUNDLE_ANALYTICS || {};
    analytics.context = analytics.context || {};
    analytics.track = analytics.track || function (eventName, values) {
      try {
        window.dataLayer = window.dataLayer || [];
        var payload = values || {};
        payload.event = eventName;
        window.dataLayer.push(payload);
      } catch (e) {}
    };
    var firstDishTracked = false;
    var halfwayTracked = false;
    var minimumTracked = false;
    var rewardEl = null;
    var rewardObserver = null;
    var rewardUnlockSeen = {};
    var pendingRewardsExpanded = false;
    var cardBox = null, cardShip = null, cardSaving = 0; /* box progress + free-delivery state for the ONE summary card (Carl 2026-09-26) */

    function trafficContext() {
      var params;
      try { params = new URLSearchParams(window.location.search || ''); } catch (e) { params = null; }
      var referrerHost = '';
      try { referrerHost = document.referrer ? new URL(document.referrer).hostname : ''; } catch (e2) {}
      return {
        bundle_source_page: SOURCE_PAGE,
        page_path: window.location.pathname || '',
        entry_mode: EDIT_BUNDLE_ID ? 'edit' : 'new',
        utm_source: params ? (params.get('utm_source') || '') : '',
        utm_medium: params ? (params.get('utm_medium') || '') : '',
        utm_campaign: params ? (params.get('utm_campaign') || '') : '',
        referrer_host: referrerHost
      };
    }
    function funnelContext(extra) {
      var data = Object.assign({}, trafficContext(), {
        starter_box_key: analytics.context.starter_box_key || 'self-built',
        starter_box: analytics.context.starter_box || 'Self-built',
        starter_box_edited: analytics.context.starter_box_edited ? 'yes' : 'no'
      }, extra || {});
      return data;
    }
    analytics.emit = function (eventName, values) { analytics.track(eventName, funnelContext(values)); };
    function track(eventName, values) { analytics.emit(eventName, values); }

    var grid = root.querySelector('[data-bb-grid]');
    var bar = root.querySelector('[data-bb-bar]') || document.querySelector('[data-bb-bar][data-bb-for="' + dataId + '"]');

    /* ---------- soup-set arrival: pool soups first, rule read off the tag ---------- */
    function poolTagOf(el) {
      var tags = String((el && el.getAttribute('data-tags')) || '').split('|');
      for (var i = 0; i < tags.length; i++) {
        if (/^ANY\s+\d+\s+FREE\s+/i.test(tags[i].trim())) return tags[i].trim();
      }
      return '';
    }
    var poolRule = null;
    if (SOUP_SET && grid) {
      var dishList = grid.querySelector('ul');
      var pooled = dishList ? Array.prototype.slice.call(dishList.children).filter(function (li) {
        return li.classList.contains('bb-dish') && !!poolTagOf(li);
      }) : [];
      if (pooled.length) {
        poolRule = parsePromoLabel(poolTagOf(pooled[0]), { name: '' });
        /* prepend in reverse so the pooled dishes keep their own relative order */
        pooled.slice().reverse().forEach(function (li) { dishList.insertBefore(li, dishList.firstChild); });
      }
    }
    function poolCount() {
      return dishes.reduce(function (sum, d) { return sum + (poolTagOf(d.el) ? getDishQty(d) : 0); }, 0);
    }
    function poolNoun(n) {
      var noun = (poolRule && poolRule.qualifierName) || 'soup';
      return n === 1 ? noun : noun + 's';
    }
    /* "2 of 6 soups" on a soup-set arrival, else null so the caller keeps its own label */
    function soupSetLabel() {
      if (!SOUP_SET || !poolRule) return null;
      return lbnT('builder.progress', '{{ n }} of {{ total }} {{ noun }}', { n: poolCount(), total: poolRule.threshold, noun: tNoun(poolNoun(poolRule.threshold)) });
    }

    /* ---------- read dishes off the rendered native cards ---------- */
    var tiles = grid ? Array.prototype.slice.call(grid.querySelectorAll('.bb-dish')) : [];
    var dishes = tiles.map(function (el) {
      var variants = parseVariants(el);
      var firstVariant = variants[0] || {};
      return {
        el: el,
        variants: variants,
        selected_variant_id: Number(el.getAttribute('data-variant-id')),
        variant_id: Number(firstVariant.id || el.getAttribute('data-variant-id')),
        price: parseFloat(firstVariant.price != null ? firstVariant.price : el.getAttribute('data-price')) || 0,
        name: el.getAttribute('data-title') || '',
        handle: el.getAttribute('data-handle') || '',
        product_type: el.getAttribute('data-product-type') || '',
        img: firstVariant.image || el.getAttribute('data-img') || '',
        qtyByVariant: {}
      };
    });
    var adding = false;
    var expanded = false;

    track('lbf_bundle_view', {
      available_dish_count: dishes.length,
      minimum_dish_count: MOQ,
      free_shipping_value: SHIP_FREE
    });

    var emptyEl = grid && grid.querySelector('[data-bb-empty]');
    if (emptyEl) emptyEl.hidden = dishes.length > 0;

    /* ---------- helpers ---------- */
    function rm(n) { return 'RM' + Number(n || 0).toFixed(2); }
    /* 17 Oct (zh-CN item 22): shopper-facing nouns and reward names through lbnT; English returns the same words. */
    var NOUN_KEYS = { 'dish': 'noun_dish', 'dishes': 'noun_dishes', 'soup': 'noun_soup', 'soups': 'noun_soups', 'promo item': 'noun_promo_item', 'promo items': 'noun_promo_items' };
    var REWARD_KEYS = { 'Shark Fin Soup': 'reward_shark_fin', 'Premium Shark Fin Soup': 'reward_premium_shark_fin', 'Minced Chicken with Szechuan Pickles': 'reward_minced_chicken', 'Basmathi Rice': 'reward_rice' };
    function tNoun(s) { var k = NOUN_KEYS[s]; return k && window.lbnT ? lbnT('builder.' + k, s) : s; }
    function tReward(s) { var k = REWARD_KEYS[s]; return k && window.lbnT ? lbnT('builder.' + k, s) : s; }
    function plural(n) { return n === 1 ? 'dish' : 'dishes'; }
    function titlePlural(n) { return n > 1 ? 'Dishes' : 'Dish'; }
    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    function getUrlParam(name) {
      try { return new URLSearchParams(window.location.search || '').get(name) || ''; }
      catch (e) { return ''; }
    }
    function cleanSourcePage() {
      try {
        var url = new URL(window.location.href);
        url.searchParams.delete('bb_edit');
        return url.pathname + url.search;
      } catch (e) {
        return location.pathname || '';
      }
    }
    function parseVariants(el) {
      var fallback = [{
        id: Number(el.getAttribute('data-variant-id')),
        title: '',
        price: parseFloat(el.getAttribute('data-price')) || 0,
        compare_at_price: parseFloat(el.getAttribute('data-compare-at-price')) || 0,
        image: el.getAttribute('data-img') || ''
      }];
      var script = el && el.querySelector('[data-bb-variants]');
      if (!script) return fallback;
      try {
        var parsed = JSON.parse(script.textContent || '[]');
        return Array.isArray(parsed) && parsed.length ? parsed.map(function (variant) {
          return {
            id: Number(variant.id),
            title: variant.title || '',
            price: parseFloat(variant.price) || 0,
            compare_at_price: parseFloat(variant.compare_at_price) || 0,
            image: variant.image || el.getAttribute('data-img') || ''
          };
        }) : fallback;
      } catch (error) {
        console.warn('[lbf-bb] variant parse failed', error);
        return fallback;
      }
    }
    function getSelectedVariant(d) {
      var selectedId = Number(d.selected_variant_id || d.variant_id);
      return d.variants.find(function (variant) { return Number(variant.id) === selectedId; }) || d.variants[0] || {
        id: d.variant_id,
        title: '',
        price: d.price,
        image: d.img
      };
    }
    function getVariantQty(d, variantId) {
      return Number(d.qtyByVariant[String(variantId)] || 0);
    }
    function setVariantQty(d, variantId, nextQty) {
      var key = String(variantId);
      var cleanQty = Math.max(0, Number(nextQty) || 0);
      if (cleanQty > 0) d.qtyByVariant[key] = cleanQty;
      else delete d.qtyByVariant[key];
    }
    function getDishQty(d) {
      return Object.keys(d.qtyByVariant).reduce(function (sum, key) {
        return sum + Number(d.qtyByVariant[key] || 0);
      }, 0);
    }
    function getDishSubtotal(d) {
      return Object.keys(d.qtyByVariant).reduce(function (sum, key) {
        var variant = d.variants.find(function (v) { return String(v.id) === key; }) || getSelectedVariant(d);
        return sum + (Number(d.qtyByVariant[key] || 0) * Number(variant.price || 0));
      }, 0);
    }
    function getBoxSaving() {
      var cents = 0;
      dishes.forEach(function (d) {
        d.variants.forEach(function (v) {
          var price = Math.round(Number(v.price || 0) * 100);
          var compare = Math.round(Number(v.compare_at_price || 0) * 100);
          if (price > 0 && compare > price) cents += (compare - price) * getVariantQty(d, v.id);
        });
      });
      return cents / 100;
    }
    function getVariantDisplayName(d, variant) {
      var title = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';
      return title ? d.name + ' - ' + title : d.name;
    }

    /* ---------- dynamic rewards: reuse existing product promo badges ---------- */
    var PROMO_LABEL_RE = /^(BUY\s+\d+\s+FREE\s+\d+|ANY\s+\d+\s+FREE\s+.+)$/i;
    /* English badge text where it is not simply the tag in capitals (badge rules in footer-group.json, 17 Oct). */
    var PROMO_TAG_LABEL = { 'any 6 free 3x rice': 'Any 6 Free 3x Rice' };
    function promoBadges(dish) {
      if (!dish || !dish.el) return [];
      return Array.prototype.slice.call(dish.el.querySelectorAll('.lbf-badge')).map(function (badge) {
        var label = String(badge.textContent || '').replace(/\s+/g, ' ').trim();
        /* 17 Oct (Carl, bug A): a translated badge ("任选6包，送3包米饭") no longer matches the rules below, so in that
           case the rule is read from the badge's tag and given today's English label. English badges are used as before. */
        if (!PROMO_LABEL_RE.test(label)) {
          var tag = String(badge.getAttribute('data-tag') || '').trim();
          if (PROMO_LABEL_RE.test(tag)) label = PROMO_TAG_LABEL[tag.toLowerCase()] || tag.toUpperCase();
        }
        var styles = window.getComputedStyle(badge);
        return {
          label: label,
          background: badge.style.backgroundColor || styles.backgroundColor,
          color: badge.style.color || styles.color
        };
      }).filter(function (badge) {
        return /^BUY\s+\d+\s+FREE\s+\d+$/i.test(badge.label) || /^ANY\s+\d+\s+FREE\s+/i.test(badge.label);
      });
    }
    function promoLabels(dish) {
      return promoBadges(dish).map(function (badge) { return badge.label; });
    }
    function cleanProductName(name) {
      return String(name || '').replace(/^\[[^\]]+\]\s*/, '').trim();
    }
    function displayProductName(name) {
      return cleanProductName(name).replace(/^[\u3400-\u9fff\uf900-\ufaff\s·&]+(?=[A-Za-z0-9])/u, '').trim();
    }
    function friendlyRewardName(name) {
      var clean = String(name || '').trim();
      if (/^shark\s*fin(\s*soup)?$/i.test(clean)) return 'Shark Fin Soup';
      if (/^minced\s*chicken/i.test(clean)) return 'Minced Chicken with Szechuan Pickles';
      if (/^rice$/i.test(clean)) return 'Basmathi Rice';
      return clean;
    }
    function friendlyQualifierName(rewardName) {
      if (/shark\s*fin/i.test(rewardName)) return 'soup';
      if (/minced\s*chicken/i.test(rewardName)) return 'soup';
      if (/rice/i.test(rewardName)) return 'dish';
      return 'promo item';
    }
    function parsePromoLabel(label, dish) {
      var buy = String(label || '').match(/^BUY\s+(\d+)\s+FREE\s+(\d+)$/i);
      if (buy) {
        return {
          type: 'buy', threshold: Number(buy[1]), rewardQty: Number(buy[2]),
          rewardName: displayProductName(dish.name), label: label
        };
      }
      var any = String(label || '').match(/^ANY\s+(\d+)\s+FREE\s+(?:(\d+)x?\s+)?(.+)$/i);
      if (any) {
        var rewardName = friendlyRewardName(any[3]);
        return {
          type: 'any', threshold: Number(any[1]), rewardQty: Number(any[2] || 1),
          rewardName: rewardName, qualifierName: friendlyQualifierName(rewardName), label: label
        };
      }
      return null;
    }
    function activeSharkOffer() {
      var offer=analytics.context.offer;
      if(!offer||offer.kind!=='shark'||String(offer.variantId)!=='49546612769074'||Number(offer.quantity)!==5)return null;
      var now=Date.now(),start=offer.startsAt?Date.parse(offer.startsAt):null,end=offer.endsAt?Date.parse(offer.endsAt):null;
      if(start!==null&&(!Number.isFinite(start)||now<start)||end!==null&&(!Number.isFinite(end)||now>=end))return null;
      return offer;
    }
    function markSharkDraftEdited(event,previousQty,nextQty) {
      if(!event.isTrusted||previousQty===nextQty||!activeSharkOffer()||analytics.context.starter_box_edited)return false;
      analytics.context.starter_box_edited=true;return true;
    }
    function requiredDishCount() {
      return MOQ;
    }
    function rewardRules() {
      var byKey = {};
      dishes.forEach(function (dish) {
        if (String(dish.product_type || '').toLowerCase() !== 'retort') return;
        promoLabels(dish).forEach(function (label) {
          var parsed = parsePromoLabel(label, dish);
          if (!parsed) return;
          var key = parsed.type === 'buy'
            ? 'buy|' + dish.handle + '|' + label.toLowerCase()
            : 'any|' + label.toLowerCase();
          if (!byKey[key]) {
            byKey[key] = Object.assign({}, parsed, { key: key, selected: 0, handles: [] });
          }
          byKey[key].selected += getDishQty(dish);
          if (byKey[key].handles.indexOf(dish.handle) === -1) byKey[key].handles.push(dish.handle);
        });
      });
      var shark=activeSharkOffer();
      if(shark){
        var dish=dishes.filter(function(d){return d.variants.some(function(v){return String(v.id)===String(shark.variantId);});})[0];
        if(dish){var key='buy|'+dish.handle+'|buy 5 free 1';
          if(!byKey[key])byKey[key]={type:'buy',threshold:5,rewardQty:1,rewardName:'Premium Shark Fin Soup',label:'BUY 5 FREE 1',key:key,selected:getVariantQty(dish,shark.variantId),handles:[dish.handle]};
        }
      }
      return Object.keys(byKey).map(function (key) {
        var rule = byKey[key];
        rule.cycles = Math.floor(rule.selected / rule.threshold);
        if (rule.maxCycles && rule.cycles >= rule.maxCycles) { rule.cycles = rule.maxCycles; rule.selected = rule.threshold * rule.maxCycles; }
        rule.unlockedQty = rule.cycles * rule.rewardQty;
        rule.unlocked = rule.unlockedQty > 0;
        rule.cycleProgress = rule.selected % rule.threshold;
        rule.remaining = rule.threshold - rule.cycleProgress;
        return rule;
      });
    }
    function ensureRewardEl() {
      if (rewardEl) return rewardEl;
      var panelInner = bar && bar.querySelector('.bb-panel-inner');
      if (!panelInner) return null;
      rewardEl = document.createElement('section');
      rewardEl.className = 'bb-rewards';
      rewardEl.setAttribute('aria-live', 'polite');
      rewardEl.hidden = true;
      rewardEl.addEventListener('click', function (event) {
        var toggle = event.target.closest('[data-bb-reward-toggle]');
        if (toggle) {
          pendingRewardsExpanded = !pendingRewardsExpanded;
          renderRewards();
          return;
        }
        var button = event.target.closest('[data-bb-reward-show]');
        if (!button) return;
        var wantedKey = String(button.getAttribute('data-bb-reward-show') || '');
        var wantedRule = rewardRules().filter(function (rule) { return rule.key === wantedKey; })[0];
        if (!wantedRule) return;
        var lines = Array.prototype.slice.call(bar.querySelectorAll('.bb-line[data-bb-handle]')).filter(function (line) {
          return wantedRule.handles.indexOf(line.getAttribute('data-bb-handle')) !== -1;
        });
        if (!lines.length) return;
        lines.forEach(function (line) { line.classList.add('bb-reward-line-focus'); });
        setTimeout(function () {
          lines[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 60);
        setTimeout(function () {
          lines.forEach(function (line) { line.classList.remove('bb-reward-line-focus'); });
        }, 2600);
      });
      return rewardEl;
    }
    function placeRewardEl() {
      var el = ensureRewardEl();
      var panelInner = bar && bar.querySelector('.bb-panel-inner');
      if (!el || !panelInner) return null;
      var starterHead = panelInner.querySelector('.lbf-sb-boxhead');
      var progressEl = panelInner.querySelector('[data-bb-boxprogress]');
      var anchorEl = progressEl || starterHead;
      if (anchorEl) {
        if (anchorEl.nextElementSibling !== el) anchorEl.insertAdjacentElement('afterend', el);
      } else if (panelInner.firstElementChild !== el) {
        panelInner.insertBefore(el, panelInner.firstChild);
      }
      return el;
    }
    function rewardItemHtml(rule) {
      return '<div class="bb-reward-earned">'
        + '<span class="bb-reward-icon" aria-hidden="true">✓</span>'
        + '<div class="bb-reward-copy"><strong>FREE ' + rule.unlockedQty + 'x ' + esc(rule.rewardName) + '</strong></div></div>';
    }
    function rewardProgressHtml(rule, compact) {
      var noun = rule.type === 'buy' ? rule.rewardName : rule.qualifierName;
      if (rule.type === 'any' && rule.remaining !== 1) noun = rule.qualifierPlural || noun.replace(/(sh|ch|s|x)$/, '$1e') + 's';
      var progress = Math.min(100, Math.round((rule.cycleProgress / rule.threshold) * 100));
      var progressCount = rule.cycleProgress;
      return '<div class="' + (compact ? 'bb-reward-pendingrow' : 'bb-reward-next') + '">'
        + (compact ? '' : '<div class="bb-reward-nextlabel">NEXT REWARD · ' + progressCount + ' OF ' + rule.threshold + '</div>')
        + '<div class="bb-reward-nextrow"><div><strong>Add ' + rule.remaining + ' more ' + esc(noun) + '</strong>'
        + '<span>' + progressCount + ' of ' + rule.threshold + ' · Get ' + rule.rewardQty + ' FREE ' + esc(rule.rewardName) + '</span></div>'
        + '<button type="button" data-bb-reward-show="' + esc(rule.key) + '">SHOW IN BOX</button></div>'
        + '<div class="bb-reward-meter"><span style="width:' + progress + '%"></span></div></div>';
    }
    function pendingRewardsHtml(rules, heading) {
      if (!rules.length) return '';
      var html = '<div class="bb-reward-more"><button type="button" data-bb-reward-toggle aria-expanded="' + (pendingRewardsExpanded ? 'true' : 'false') + '">'
        + '<span>' + esc(heading || 'OTHER REWARD PROGRESS') + ' (' + rules.length + ')</span><span aria-hidden="true">' + (pendingRewardsExpanded ? '−' : '+') + '</span></button>';
      if (pendingRewardsExpanded) {
        html += '<div class="bb-reward-pending">';
        rules.forEach(function (rule) { html += rewardProgressHtml(rule, true); });
        html += '</div>';
      }
      return html + '</div>';
    }
    function unlockedActionsHtml(rules) {
      var html = '<div class="bb-reward-actions">'
        + '<div class="bb-reward-note">Gifts are auto added upon checkout.</div>';
      if (rules.length) {
        html += '<button type="button" data-bb-reward-toggle aria-expanded="' + (pendingRewardsExpanded ? 'true' : 'false') + '">'
          + '<span>VIEW NEXT REWARD</span><span aria-hidden="true">' + (pendingRewardsExpanded ? '−' : '+') + '</span></button>';
        if (pendingRewardsExpanded) {
          html += '<div class="bb-reward-pending">';
          rules.forEach(function (rule) { html += rewardProgressHtml(rule, true); });
          html += '</div>';
        }
      }
      return html + '</div>';
    }
    function getShippingCart() {
      var items = [];
      dishes.forEach(function (d) {
        d.variants.forEach(function (v) {
          var q = getVariantQty(d, v.id);
          if (q > 0) items.push({ qty: q, linePrice: Math.round(v.price * 100) * q, frozenTag: false, retortType: true, name: getVariantDisplayName(d, v), image: v.image || d.img });
        });
      });
      return { items: items, totalPrice: items.reduce(function (sum, item) { return sum + item.linePrice; }, 0) };
    }

    function deliveryHTML() {
      if (!cardShip || !getShippingCart().items.length) return '';
      var east = cardShip.kind === 'eastmsia';
      var free = cardShip.kind === 'free';
      var type = east ? 'info' : (free ? 'ok' : 'warn');
      var label = east ? lbnT('ship.chip_east_msia', 'East Msia') : lbnT('ship.chip_west_msia', 'West Msia');
      var message = east ? lbnT('ship.east_weight_based', 'Weight-based · exact rate at checkout') : (free ? lbnT('ship.you_enjoy_free_delivery', 'You enjoy free delivery') : lbnT('ship.more_for_free_delivery', '{{ amount }} more for FREE delivery', { amount: rm(cardShip.remaining) }));
      return '<div class="bb-delivery"><div class="lbfcb-sbox is-' + type + '"><span class="lbfcb-sic" aria-hidden="true">' + (free ? '✓' : 'i') + '</span><span class="lbfcb-msg">' + message + '</span><button type="button" class="lbfcb-chipbtn" data-bb-region aria-haspopup="dialog" aria-label="' + esc(lbnT('builder.delivery_area', 'Delivery area: {{ name }}', { name: label })) + '">' + label + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg></button></div></div>';
    }

    function renderRewards() {
      var el = placeRewardEl();
      if (!el) return;
      var selectedTotal = dishes.reduce(function (sum, dish) { return sum + getDishQty(dish); }, 0);
      var rules = rewardRules();
      var untouchedPromo88 = analytics.context.starter_box_key === 'promo88'
        && !analytics.context.starter_box_edited
        && !!bar.querySelector('.lbf-sb-boxhead');
      if (untouchedPromo88) { el.hidden = true; el.innerHTML = ''; return; }
      var unified = !!(window.LBF_CART && LBF_CART.summaryHTML);
      if (!selectedTotal || (!rules.length && !unified)) { el.hidden = true; el.innerHTML = ''; return; }
      var unlocked = rules.filter(function (rule) { return rule.unlocked; });
      var activeProgress = rules.filter(function (rule) {
        return rule.selected > 0 && (!rule.unlocked || rule.cycleProgress > 0);
      })
        .sort(function (a, b) { return a.remaining - b.remaining || b.selected - a.selected; });
      var hasProgressToggle = unlocked.length ? activeProgress.length > 0 : activeProgress.length > 1;
      if (!hasProgressToggle) pendingRewardsExpanded = false;
      if (!unlocked.length && !activeProgress.length && !unified) {
        el.hidden = true;
        el.innerHTML = '';
        return;
      }
      /* Carl 2026-09-26: rewards look and read exactly like the cart summary card - green-tick wins, ONE progress
         line with the gold segments, the rest behind "Show all" (drawn by LBF_CART.summaryHTML, the same code). */
      if (window.LBF_CART && LBF_CART.summaryHTML) {
        unlocked.forEach(function (rule) {
          if (Number(rewardUnlockSeen[rule.key] || 0) < rule.unlockedQty) {
            rewardUnlockSeen[rule.key] = rule.unlockedQty;
            track('lbf_bundle_reward_unlocked', { promotion_label: rule.label, reward_name: rule.rewardName, reward_quantity: rule.unlockedQty, qualifying_quantity: rule.selected });
          }
        });
        var rows = [];
        if (cardShip) rows.push(cardShip.kind === 'eastmsia'
          ? { ok: false, pri: 1, t: lbnT('builder.east_row', 'East Msia: weight-based delivery, exact rate at checkout') }
          : (cardShip.kind === 'free'
            ? { ok: true, rank: 1, t: lbnT('ship.you_enjoy_free_delivery', 'You enjoy free delivery') }
            : { ok: false, pri: 1, t: lbnT('ship.more_for_free_delivery', '{{ amount }} more for FREE delivery', { amount: rm(cardShip.remaining) }), pct: cardShip.pct }));
        /* the box is the checkout blocker here (like the frozen minimum on the cart): it leads until it is complete */
        if (cardBox && cardBox.need > 0) rows.push({ ok: false, block: true, t: lbnT('builder.need', 'Add {{ n }} more {{ noun }} to complete your box', { n: cardBox.need, noun: tNoun(cardBox.need === 1 ? cardBox.one : cardBox.many) }), count: lbnT('builder.count', '{{ n }} of {{ total }}', { n: cardBox.now, total: cardBox.total }), seg: cardBox.total <= 12 ? [cardBox.now, cardBox.total] : null, pct: cardBox.total > 12 ? cardBox.now / cardBox.total : null });

        rows = rows.concat(unlocked.map(function (rule) {
          return { ok: true, rank: 2, t: lbnT('builder.reward_unlocked', 'FREE {{ qty }}{{ name }} unlocked, added at checkout', { qty: rule.unlockedQty > 1 ? rule.unlockedQty + 'x ' : '', name: tReward(rule.rewardName) }) };
        }).concat(activeProgress.map(function (rule) {
          var noun = rule.type === 'buy' ? rule.rewardName : rule.qualifierName;
          if (rule.type === 'any' && rule.remaining !== 1) noun = rule.qualifierPlural || noun.replace(/(sh|ch|s|x)$/, '$1e') + 's';
          var r = { ok: false, pri: 2, t: lbnT('builder.reward_progress', 'Add {{ n }} more {{ noun }}, get {{ qty }} FREE {{ name }}', { n: rule.remaining, noun: rule.type === 'buy' ? tReward(noun) : tNoun(noun), qty: rule.rewardQty, name: tReward(rule.rewardName) }), count: lbnT('builder.count', '{{ n }} of {{ total }}', { n: rule.cycleProgress, total: rule.threshold }) };
          if (rule.threshold <= 12) r.seg = [rule.cycleProgress, rule.threshold]; else r.pct = rule.cycleProgress / rule.threshold;
          return r;
        })));
        if (cardSaving > 0) rows.push({ ok: true, rank: 3, t: lbnT('builder.box_saving', 'You save {{ amount }} on this box', { amount: rm(cardSaving) }) });
        el.innerHTML = rows.length ? '<div class="lbf-cj-summary bb-rewards-sum">' + LBF_CART.summaryHTML(rows, 1, { cap: 3 }) + '</div>' : '';
        el.hidden = false;
        return;
      }
      if (!renderRewards.__waited) { renderRewards.__waited = true; document.addEventListener('DOMContentLoaded', function () { renderRewards(); }); }
      var giftCount = unlocked.reduce(function (sum, rule) { return sum + rule.unlockedQty; }, 0);
      var status = giftCount ? giftCount + ' UNLOCKED' : (activeProgress.length ? 'KEEP BUILDING' : 'NOT YET');
      var html = '<div class="bb-reward-head"><strong>YOUR REWARDS</strong><span class="' + (giftCount ? 'is-unlocked' : '') + '">' + status + '</span></div>';
      unlocked.forEach(function (rule) {
        html += rewardItemHtml(rule);
        if (Number(rewardUnlockSeen[rule.key] || 0) < rule.unlockedQty) {
          rewardUnlockSeen[rule.key] = rule.unlockedQty;
          track('lbf_bundle_reward_unlocked', {
            promotion_label: rule.label,
            reward_name: rule.rewardName,
            reward_quantity: rule.unlockedQty,
            qualifying_quantity: rule.selected
          });
        }
      });
      if (unlocked.length) {
        html += unlockedActionsHtml(activeProgress);
      } else {
        if (activeProgress.length) html += rewardProgressHtml(activeProgress[0], false);
        if (activeProgress.length > 1) html += pendingRewardsHtml(activeProgress.slice(1), 'OTHER REWARD PROGRESS');
      }
      el.innerHTML = html;
      el.hidden = false;
    }
    function watchRewardBadges() {
      if (rewardObserver || !grid) return;
      rewardObserver = new MutationObserver(function (mutations) {
        var relevant = mutations.some(function (mutation) {
          return Array.prototype.slice.call(mutation.addedNodes || []).some(function (node) {
            return node.nodeType === 1 && (node.matches('.lbf-badge, .lbf-badges') || node.querySelector('.lbf-badge'));
          });
        });
        if (relevant) { renderLines(); renderRewards(); }
      });
      rewardObserver.observe(grid, { childList: true, subtree: true });
      setTimeout(renderRewards, 500);
      setTimeout(renderRewards, 1400);
    }
    document.addEventListener('lbf:bundle-rewards-refresh', renderRewards);
    var contextResetAbort=new AbortController();
    document.addEventListener('lbf:badges-ready', function(){if(root.isConnected)renderLines();},{signal:contextResetAbort.signal});
    document.addEventListener('lbf:bundle-context-reset', function(){if(root.isConnected)render();},{signal:contextResetAbort.signal});
    document.addEventListener('shopify:section:unload', function(e){if(e.target.contains(root))contextResetAbort.abort();},{signal:contextResetAbort.signal});

    /* ---------- neutralise native card so the box flow drives selection ---------- */
    /* Disable the card's link navigation (clicking the card body must NOT navigate),
       and disable the native quick-add submit (the box uses the stepper instead).
       The eye icon + the native quick-VIEW modal are intentionally left working. */
    dishes.forEach(function (d) {
      var el = d.el;
      // kill product-page links inside the native card (title + image links)
      el.querySelectorAll('a.full-unstyled-link, a[href*="/products/"]').forEach(function (a) {
        a.setAttribute('tabindex', '-1');
        a.setAttribute('aria-disabled', 'true');
        a.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); });
      });
      // hide the native quick-add control entirely — box add is the stepper.
      // (we keep our own hidden .bb-native-qv opener for the quick-VIEW popup.)
      el.querySelectorAll('.card__content > .quick-add, .card__content > quick-add-bulk').forEach(function (q) {
        q.classList.add('bb-hide');
      });
      el.querySelectorAll('product-form .quick-add__submit, .quick-add > modal-opener, .quick-add > product-form').forEach(function (q) {
        // belt & suspenders: prevent any native add submit from firing
        q.addEventListener('click', function (e) {
          if (e.target.closest('.bb-native-qv')) return; // never block the quick-view opener
          e.preventDefault();
          e.stopPropagation();
        }, true);
      });
    });

    /* ---------- move pick controls inside card + fix qv buttons ---------- */
    dishes.forEach(function (d) {
      var el = d.el;
      /* Remove aria-hidden from qv opener so Dawn's ModalOpener can find the button */
      var qvBtn = el.querySelector('.bb-native-qv__open');
      if (qvBtn) qvBtn.removeAttribute('aria-hidden');
      /* Move the pick control inside the SECOND .card__content (the one outside
         .card__inner that holds .card__information + .quick-add). The FIRST
         .card__content lives inside .card__inner and overlays the image — putting
         the button there makes it float over the photo. */
      var pick = el.querySelector('[data-bb-pick]');
      var allCC = el.querySelectorAll('.card__content');
      var cardContent = allCC.length > 1 ? allCC[allCC.length - 1] : (allCC[0] || null);
      if (pick && cardContent) cardContent.appendChild(pick);
      var overlay = el.querySelector('.bb-pick-overlay');
      var cardInner = el.querySelector('.card__inner');
      if (overlay && cardInner) cardInner.appendChild(overlay);
    });

    /* ---------- promo tag badges ---------- */
    /* Handled automatically by product-badges.js (loaded in the section).
       It finds .card-wrapper elements via MutationObserver, reads the product
       handle from the card link, and injects .lbf-badge pills into .card__media.
       No custom code needed here. */

    /* ---------- per-tile builder controls ---------- */
    dishes.forEach(function (d, i) {
      var el = d.el;
      var pick = el.querySelector('[data-bb-pick]');
      var view = el.querySelector('[data-bb-view]');
      var variantCards = Array.prototype.slice.call(el.querySelectorAll('[data-bb-variant-card]'));

      variantCards.forEach(function (card) {
        card.addEventListener('click', function () {
          d.selected_variant_id = Number(card.getAttribute('data-variant-id'));
          render();
        });
      });

      if (pick) {
        pick.addEventListener('click', function (e) {
          var b = e.target.closest('button'); if (!b) return;
          if (b.hasAttribute('data-bb-variant-card')) return;
          var variant = getSelectedVariant(d);
          var selectedQty = getVariantQty(d, variant.id);
          if (b.hasAttribute('data-bb-add') || b.hasAttribute('data-bb-inc')) {
            setVariantQty(d, variant.id, selectedQty + 1);
            /* Flash the "+" button yellow on tap */
            var inc = pick.querySelector('[data-bb-inc]');
            if (inc && window.LBF_CART && LBF_CART.flash) LBF_CART.flash(inc);
          }
          else if (b.hasAttribute('data-bb-dec')) setVariantQty(d, variant.id, selectedQty - 1);
          render();
        });
      }

      if (view) {
        view.addEventListener('pointerenter', function () { prefetchQuickView(el); }, { passive: true });
        view.addEventListener('focusin', function () { prefetchQuickView(el); });
        view.addEventListener('touchstart', function () { prefetchQuickView(el); }, { passive: true });
        view.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          track('lbf_bundle_product_view', { product_handle: d.handle, product_name: d.name });
          openNativeQuickView(el);
        });
      }
    });
    scheduleQuickViewPrefetches();
    watchRewardBadges();
    loadEditBundle();

    /* ---------- native quick-view: open the Dawn quick-add-modal ---------- */
    function openNativeQuickView(tile) {
      /* Strategy: try modal.show() first (QuickAddModal), catch any error
         (e.g. loading-spinner class mismatch), fall back to clicking the
         ModalOpener button, and finally navigate to the product page. */
      var mo = tile.querySelector('modal-opener.bb-native-qv');
      var modalId = mo && mo.getAttribute('data-modal');
      var modal = modalId && document.querySelector(modalId);
      var opener = mo && mo.querySelector('button');

      if (modal && opener) {
        if (!modal.__lbfInfoOnly) {
          var preprocess = modal.preprocessHTML;
          modal.preprocessHTML = function (productElement) {
            // The PDP footer owns these rules; retain its data with the fetched preview.
            var criteria = productElement.ownerDocument.getElementById('lbf-hcl-criteria-data');
            if (criteria && !document.getElementById('lbf-hcl-criteria-data')) {
              // Keep the authoritative data through native modal content cleanup/reopening.
              document.body.appendChild(criteria.cloneNode(true));
            }
            if (preprocess) preprocess.call(this, productElement);
            productElement.querySelectorAll('.lbf-tray, product-form, variant-selects, variant-radios, .product-form__quantity, .shopify-payment-button, .lbf-pdp-card-separator:has(+ .lbf-tray)').forEach(function (control) { control.remove(); });
          };
          var hide = modal.hide;
          modal.hide = function (preventFocus) {
            this.querySelectorAll('.lbf-rv-wrap').forEach(function (wrap) {
              if (wrap.lbfStopReviews) wrap.lbfStopReviews();
            });
            this.openedBy = tile.querySelector('[data-bb-view]') || this.openedBy;
            return hide.call(this, preventFocus);
          };
          // During HTML insertion the viewer can upgrade before the thumbnail slider.
          modal.addEventListener('slideChanged', function (event) {
            var gallery = event.target.closest('media-gallery');
            var thumbnails = gallery && gallery.querySelector('[id^="GalleryThumbnails"]');
            if (thumbnails && typeof thumbnails.isSlideVisible !== 'function') event.stopPropagation();
          }, true);
          modal.__lbfInfoOnly = true;
        }
        var normalize = function () {
          requestAnimationFrame(function () { normalizeQuickViewGallery(modal); });
          setTimeout(function () { normalizeQuickViewGallery(modal); }, 160);
        };
        if (openCachedQuickView(modal, opener, normalize)) return;
        try {
          if (typeof modal.show === 'function') { modal.show(opener); normalize(); return; }
        } catch (e) { console.warn('[lbf-bb] modal.show failed, trying click', e); }
        try { opener.click(); normalize(); return; } catch (e2) {}
      }

      /* Also try the NATIVE quick-add modal-opener rendered by card-product
         (inside the .quick-add div we hid). It may be wired up by Dawn. */
      var nativeMO = tile.querySelector('.quick-add modal-opener button, .quick-add product-form modal-opener button');
      if (nativeMO) { try { nativeMO.click(); return; } catch (e3) {} }

      /* Last resort: navigate to the product page */
      var handle = tile.getAttribute('data-handle');
      if (handle) window.location.href = '/products/' + handle;
    }

    function getQuickViewUrl(tile) {
      var opener = tile && tile.querySelector('modal-opener.bb-native-qv button');
      return opener && opener.getAttribute('data-product-url');
    }

    function prefetchQuickView(tile) {
      var url = getQuickViewUrl(tile);
      return prefetchQuickViewUrl(url);
    }

    function prefetchQuickViewUrl(url) {
      if (!url || quickViewCache[url]) return quickViewCache[url];
      quickViewCache[url] = { html: null, promise: null };
      var fetchUrl = url;
      if (window.Shopify && Shopify.previewMode && Shopify.theme && Shopify.theme.id) {
        var previewUrl = new URL(url, window.location.origin);
        previewUrl.searchParams.set('preview_theme_id', Shopify.theme.id);
        fetchUrl = previewUrl.toString();
      }
      quickViewCache[url].promise = fetch(fetchUrl)
        .then(function (response) { return response.text(); })
        .then(function (html) {
          quickViewCache[url].html = html;
          return html;
        })
        .catch(function (error) {
          delete quickViewCache[url];
          throw error;
        });
      return quickViewCache[url];
    }

    function scheduleQuickViewPrefetches() {
      var queue = tiles.slice();
      var run = function () {
        var tile = queue.shift();
        if (tile) prefetchQuickView(tile);
        if (queue.length) window.setTimeout(run, 180);
      };
      var start = function () { window.setTimeout(run, 600); };
      if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 1800 });
      else start();
    }

    function openCachedQuickView(modal, opener, afterOpen) {
      var url = opener && opener.getAttribute('data-product-url');
      if (!modal || !opener || !url) return false;
      var entry = quickViewCache[url] || prefetchQuickViewUrl(url);
      var showFromHtml = function (html) {
        var responseHTML = new DOMParser().parseFromString(html, 'text/html');
        var productElement = responseHTML.querySelector('product-info');
        if (!productElement) throw new Error('No product-info found for quick view.');

        dedupeQuickViewHealthPanels(productElement);
        if (typeof modal.preprocessHTML === 'function') modal.preprocessHTML(productElement);
        if (typeof HTMLUpdateUtility !== 'undefined' && typeof HTMLUpdateUtility.setInnerHTML === 'function') {
          HTMLUpdateUtility.setInnerHTML(modal.modalContent, productElement.outerHTML);
        } else {
          modal.modalContent.innerHTML = productElement.outerHTML;
        }

        if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
        if (window.ProductModel) window.ProductModel.loadShopifyXR();
        modal.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

        modal.openedBy = opener;
        document.body.classList.add('overflow-hidden');
        modal.setAttribute('open', '');
        if (typeof trapFocus === 'function') trapFocus(modal, modal.querySelector('[role="dialog"]'));
        if (window.pauseAllMedia) window.pauseAllMedia();
        if (afterOpen) afterOpen();
      };

      if (entry && entry.html) {
        try { showFromHtml(entry.html); return true; }
        catch (error) { console.warn('[lbf-bb] cached quick-view failed, falling back', error); return false; }
      }

      if (entry && entry.promise) {
        entry.promise
          .then(showFromHtml)
          .catch(function (error) {
            console.warn('[lbf-bb] prefetched quick-view failed, falling back', error);
            try { if (typeof modal.show === 'function') modal.show(opener); }
            catch (fallbackError) { console.warn('[lbf-bb] fallback quick-view failed', fallbackError); }
          });
        return true;
      }

      return false;
    }

    function dedupeQuickViewHealthPanels(productElement) {
      var panels = productElement ? productElement.querySelectorAll('[data-lbf-health-root]') : [];
      if (!panels || panels.length < 2) return;
      panels.forEach(function (panel, index) {
        if (index > 0) panel.remove();
      });
    }

    /* ---------- bottom bar events ---------- */
    if (bar) {
      bar.addEventListener('click', function (event) {
        if (!event.target.closest('[data-bb-region]')) return;
        if (window.LBF_SHIPPING_POPUP) {
          var opener = bar.querySelector('[data-bb-toggle]');
          expanded = false;
          syncExpanded();
          window.LBF_SHIPPING_POPUP.open({ cart: getShippingCart(), step: 1, trigger: 'region', reason: 'manual', opener: opener });
        }
      });
      document.addEventListener('lbf:shipping-state-change', function () { render(); });
      var toggle = bar.querySelector('[data-bb-toggle]');
      if (toggle) toggle.addEventListener('click', function () { expanded = !expanded; syncExpanded(); });
      var close = bar.querySelector('[data-bb-close]');
      if (close) close.addEventListener('click', function () { expanded = false; syncExpanded(); });
      var backdrop = bar.querySelector('[data-bb-backdrop]');
      if (backdrop) backdrop.addEventListener('click', function () { expanded = false; syncExpanded(); });
      var cta = bar.querySelector('[data-bb-cta]');
      if (cta) cta.addEventListener('click', onAddToCart);
      var lines = bar.querySelector('[data-bb-lines]');
      if (lines) {
        lines.addEventListener('click', function (e) {
          var b = e.target.closest('button'); if (!b) return;
          var i = +b.getAttribute('data-i'), a = b.getAttribute('data-act');
          var variantId = Number(b.getAttribute('data-variant-id'));
          if (!dishes[i] || !variantId) return;
          var lineQty = getVariantQty(dishes[i], variantId);
          if (a === 'linc') setVariantQty(dishes[i], variantId, lineQty + 1);
          if (a === 'ldec') setVariantQty(dishes[i], variantId, lineQty - 1);
          if (a === 'lrm') setVariantQty(dishes[i], variantId, 0);
          var sharkEdited=markSharkDraftEdited(e,lineQty,getVariantQty(dishes[i],variantId));
          render();
          if(sharkEdited)document.dispatchEvent(new CustomEvent('lbf:bundle-draft-edit'));
        });
      }
    }
    function syncExpanded() {
      if (!bar) return;
      var floating = !window.matchMedia('(min-width:990px)').matches;
      bar.classList.toggle('expanded', expanded);
      document.documentElement.classList.toggle('lbf-bb-drawer-open', expanded && floating);
      document.body.classList.toggle('lbf-bb-drawer-open', expanded && floating);
      var t = bar.querySelector('[data-bb-toggle]');
      if (t) t.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      var panel = bar.querySelector('.bb-panel');
      var shell = bar.querySelector('.bb-barwrap');
      if (panel && shell) {
        panel.inert = floating && !expanded;
        var wasModal = shell.getAttribute('aria-modal') === 'true';
        shell.setAttribute('role', expanded && floating ? 'dialog' : 'region');
        shell.setAttribute('aria-label', lbnT('builder.box_title', 'Your Bundle Box'));
        if (expanded && floating) {
          shell.setAttribute('aria-modal', 'true');
          if (typeof window.trapFocus === 'function') window.trapFocus(shell, bar.querySelector('[data-bb-close]'));
        } else {
          shell.removeAttribute('aria-modal');
          if (wasModal && typeof window.removeTrapFocus === 'function') window.removeTrapFocus(floating ? t : null);
        }
      }
      render();
    }
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !expanded || document.querySelector('quick-add-modal[open],dialog[open],cart-drawer.active,.lbf-sheet.is-open')) return;
      event.preventDefault();
      expanded = false;
      syncExpanded();
    });
    window.matchMedia('(min-width:990px)').addEventListener('change', syncExpanded);

    /* ---------- render ---------- */
    function render() {
      var minimum=requiredDishCount();
      var count = 0, sub = 0;
      dishes.forEach(function (d) {
        var variant = getSelectedVariant(d);
        var q = getVariantQty(d, variant.id);
        var dishQty = getDishQty(d);
        count += dishQty;
        sub += getDishSubtotal(d);
        d.variant_id = Number(variant.id);
        d.price = Number(variant.price || 0);
        d.img = variant.image || d.img;
        d.el.setAttribute('data-variant-id', d.variant_id);
        d.el.setAttribute('data-price', d.price);
        var el = d.el;
        el.querySelectorAll('[data-bb-variant-card]').forEach(function (card) {
          var selected = Number(card.getAttribute('data-variant-id')) === d.variant_id;
          card.classList.toggle('is-selected', selected);
          card.setAttribute('aria-checked', selected ? 'true' : 'false');
        });
        var cntEl = el.querySelector('[data-bb-cnt]');
        var qbEl = el.querySelector('[data-bb-qbadge]');
        if (cntEl) cntEl.textContent = q;
        if (qbEl) qbEl.textContent = dishQty;
        el.classList.toggle('sel', dishQty > 0);
      });

      var uniqueDishCount = dishes.reduce(function (sum, dish) { return sum + (getDishQty(dish) > 0 ? 1 : 0); }, 0);
      if (count > 0 && !firstDishTracked) {
        firstDishTracked = true;
        track('lbf_bundle_first_dish', { dish_count: count, unique_dish_count: uniqueDishCount, bundle_value: sub });
      }
      if (count >= Math.ceil(minimum / 2) && !halfwayTracked) {
        halfwayTracked = true;
        track('lbf_bundle_halfway', { dish_count: count, unique_dish_count: uniqueDishCount, bundle_value: sub });
      }
      if (count >= minimum && !minimumTracked) {
        minimumTracked = true;
        track('lbf_bundle_minimum_reached', { dish_count: count, unique_dish_count: uniqueDishCount, bundle_value: sub });
      }

      cardSaving = getBoxSaving();
      renderRewards();

      var setLabel = soupSetLabel();
      var fc = root.querySelector('[data-bb-fillcount]');
      if (fc) fc.textContent = setLabel || (count === 1 ? lbnT('builder.added_one', '{{ n }} dish added', { n: count }) : lbnT('builder.added_many', '{{ n }} dishes added', { n: count }));

      if (!bar) return;
      var tc = bar.querySelector('[data-bb-tcount]');
      if (tc) tc.textContent = count;
      var tl = bar.querySelector('[data-bb-tlabel]');
      if (tl) tl.textContent = setLabel || (count > 1 ? lbnT('builder.title_added_many', '{{ n }} Dishes Added', { n: count }) : lbnT('builder.title_added_one', '{{ n }} Dish Added', { n: count }));

      /* Box progress, first thing in the panel (above the shipping line) until the box is
         complete. On a soup-set arrival it counts the pool soups against the Any-N rule. */
      var prog = bar.querySelector('[data-bb-boxprogress]');
      if (prog) {
        var pTotal = minimum, pNow = count, pNounMany = 'dishes', pNounOne = 'dish';
        if (SOUP_SET && poolRule) {
          pTotal = poolRule.threshold; pNow = poolCount();
          pNounMany = poolNoun(2); pNounOne = poolNoun(1);
        }
        var pNeed = Math.max(0, pTotal - pNow);
        var pPct = Math.min(100, Math.round((pNow / pTotal) * 100));
        prog.hidden = count >= minimum;
        prog.innerHTML = '<div class="bb-boxprogress__row"><strong>' + esc(lbnT('builder.progress', '{{ n }} of {{ total }} {{ noun }}', { n: pNow, total: pTotal, noun: tNoun(pNounMany) })) + '</strong>'
          + '<span>' + esc(pNeed ? lbnT('builder.need', 'Add {{ n }} more {{ noun }} to complete your box', { n: pNeed, noun: tNoun(pNeed === 1 ? pNounOne : pNounMany) }) : lbnT('builder.complete', 'Box complete')) + '</span></div>'
          + '<div class="bb-boxprogress__meter"><span style="width:' + pPct + '%"></span></div>';
      }
      bar.querySelectorAll('[data-bb-subtotal]').forEach(function (st) { st.textContent = rm(sub); });

      /* CTA states: below minimum -> disabled "Add N More Dishes"; at/above -> active completion CTA. */
      var cta = bar.querySelector('[data-bb-cta]');
      if (cta && !adding) {
        if (count < minimum) {
          var need = minimum - count;
          cta.disabled = true;
          cta.textContent = need > 1 ? lbnT('builder.cta_more_many', 'Add {{ n }} More Dishes', { n: need }) : lbnT('builder.cta_more_one', 'Add {{ n }} More Dish', { n: need });
        } else {
          cta.disabled = false;
          cta.textContent = lbnT('builder.cta_complete', 'Complete Your Bundle');
        }
      }

      /* Delivery has one shared summary; the compact strip is only for the collapsed mobile bar. */
      var insideShip = bar.querySelector('[data-bb-shipline-inside]');
      var outsideShip = bar.querySelector('[data-bb-shipline-outside]');
      var outsideRow = bar.querySelector('[data-bb-progress-row]');
      if (insideShip) { insideShip.hidden = true; insideShip.innerHTML = ''; }
      if (outsideShip) { outsideShip.hidden = true; outsideShip.innerHTML = ''; }
      if (outsideRow) outsideRow.hidden = true;

      /* Carl 2026-09-26: inside the box, ONE card like the cart - box progress, free delivery and gifts together */
      if (window.LBF_CART && LBF_CART.summaryHTML) {
        var cbTotal = minimum, cbNow = count, cbMany = 'dishes', cbOne = 'dish';
        if (SOUP_SET && poolRule) { cbTotal = poolRule.threshold; cbNow = poolCount(); cbMany = poolNoun(2); cbOne = poolNoun(1); }
        cardBox = { total: cbTotal, now: cbNow, need: Math.max(0, cbTotal - cbNow), many: cbMany, one: cbOne };
        var shippingResult = SHIP && SHIP.compute(getShippingCart(), SHIP.activeRegion(false) || 'west-msia', {});
        cardShip = { kind: shippingResult ? shippingResult.kind : (sub >= SHIP_FREE ? 'free' : 'progress'), remaining: shippingResult && shippingResult.remaining != null ? shippingResult.remaining / 100 : Math.max(0, SHIP_FREE - sub), pct: Math.min(1, sub / SHIP_FREE) };
        if (prog) { prog.hidden = true; }
        if (insideShip) { insideShip.hidden = true; insideShip.innerHTML = ''; }
        if (outsideShip) {
          outsideShip.hidden = false;
          outsideShip.innerHTML = deliveryHTML();
          if (outsideRow) outsideRow.hidden = expanded || window.matchMedia('(min-width:990px)').matches || count < minimum;
        }
        renderRewards();
      }

      renderLines();
    }

    function renderLines() {
      var lines = bar && bar.querySelector('[data-bb-lines]');
      if (!lines) return;
      var active = lines.contains(document.activeElement) ? document.activeElement : null;
      var focusVariant = active && active.getAttribute('data-variant-id');
      var focusAction = active && active.getAttribute('data-act');
      var html = '';
      dishes.forEach(function (d, i) {
        d.variants.forEach(function (variant) {
          var lineQty = getVariantQty(d, variant.id);
          if (lineQty <= 0) return;
          var lineName = getVariantDisplayName(d, variant);
          var lineTags = String(d.el.getAttribute('data-tags') || '').split('|').filter(Boolean);
          var linePromos = window.LBF_BADGES ? window.LBF_BADGES.inline(lineTags) : '';
          html += '<div class="bb-line" data-bb-handle="' + esc(d.handle) + '">'
            + (variant.image ? '<img src="' + esc(variant.image) + '" alt="' + esc(lineName) + '" loading="lazy">' : '')
            + '<div class="bb-li"><div class="bb-lnm">' + esc(lineName) + '</div>'
            + (linePromos ? '<div class="bb-line-promos">' + linePromos + '</div>' : '')
            + '<div class="bb-lpr">' + esc(lbnT('builder.each', '{{ amount }} each', { amount: rm(variant.price) })) + (variant.price > 0 && variant.compare_at_price > variant.price ? '<s>' + rm(variant.compare_at_price) + '</s>' : '') + '</div></div>'
            + '<div class="bb-lqty lbf-stp lbf-stp--cart">'
            + '<button type="button" class="lbf-stp__m" data-act="ldec" data-i="' + i + '" data-variant-id="' + variant.id + '" aria-label="' + esc(lbnT('builder.remove_one', 'Remove one')) + '">−</button>'
            + '<span class="bb-lc lbf-stp__n">' + lineQty + '</span>'
            + '<button type="button" class="lbf-stp__p" data-act="linc" data-i="' + i + '" data-variant-id="' + variant.id + '" aria-label="' + esc(lbnT('builder.add_one', 'Add one')) + '">+</button>'
            + '</div>'
            + '<span class="bb-line-total">' + rm(variant.price * lineQty) + '</span>'
            + '<button type="button" class="bb-trash" data-act="lrm" data-i="' + i + '" data-variant-id="' + variant.id + '" aria-label="' + esc(lbnT('builder.remove_line', 'Remove {{ name }}', { name: lineName })) + '"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 10v7M14 10v7"/></svg></button>'
            + '</div>';
        });
      });
      lines.innerHTML = html || '<div class="bb-emptyline">' + esc(lbnT('builder.empty_lines', 'No dishes added yet. Tap "+ Add to box" on a dish.')) + '</div>';
      if (focusVariant && focusAction) {
        var nextFocus = lines.querySelector('[data-variant-id="' + focusVariant + '"][data-act="' + focusAction + '"]') || bar.querySelector('[data-bb-close]');
        if (nextFocus) nextFocus.focus({ preventScroll: true });
      }
    }

    /* ---------- add to cart ---------- */
    function buildLineItems() {
      var items = [];
      var props = {
        _bundle_id: SESSION_ID,
        _bundle_name: BUNDLE_NAME,
        _bundle_source_page: SOURCE_PAGE,
        'Bundle': BUNDLE_NAME
      };
      dishes.forEach(function (d) {
        Object.keys(d.qtyByVariant).forEach(function (variantId) {
          var q = Number(d.qtyByVariant[variantId] || 0);
          if (q > 0) {
            var lineProps=Object.assign({},props), offer=analytics.context.offer;
            if(offer && (String(offer.variantId)===String(variantId)||(offer.variantIds||[]).indexOf(String(variantId))>=0) && (offer.kind!=='shark'||activeSharkOffer())){
              Object.assign(lineProps,{_lbf_offer_key:offer.key,_lbf_offer_name:offer.name,_lbf_offer_source:'Bundle page',_lbf_offer_mode:analytics.context.starter_box_edited?'Builder edited':'Recommended selection',_lbf_offer_version:'offer-lines-v2'});
            } else {
              /* Carl 2026-10-13: every dish records which card it came from. A preset box names itself; an offer
                 preset's other dishes and a builder started from scratch are the customer's own picks. */
              var preset=analytics.context.starter_box_key&&analytics.context.starter_box_key!=='self-built'&&!offer;
              Object.assign(lineProps,preset
                ?{_lbf_offer_key:analytics.context.starter_box_key,_lbf_offer_name:analytics.context.starter_box||BUNDLE_NAME,_lbf_offer_source:'Bundle page',_lbf_offer_mode:analytics.context.pick_own?'Pick your own':analytics.context.starter_box_edited?'Builder edited':'Recommended selection',_lbf_offer_version:'offer-lines-v2'}
                :{_lbf_offer_key:'self-built',_lbf_offer_name:BUNDLE_NAME,_lbf_offer_source:'Bundle page',_lbf_offer_mode:'Pick your own',_lbf_offer_version:'offer-lines-v2'});
            }
            items.push({ id:Number(variantId),quantity:q,properties:lineProps });
          }
        });
      });
      return items;
    }

    function onAddToCart() {
      var count = dishes.reduce(function (s, d) { return s + getDishQty(d); }, 0);
      if (count < requiredDishCount() || adding) return;
      var subtotal = dishes.reduce(function (s, d) { return s + getDishSubtotal(d); }, 0);
      var uniqueCount = dishes.reduce(function (s, d) { return s + (getDishQty(d) > 0 ? 1 : 0); }, 0);
      var cta = bar.querySelector('[data-bb-cta]');
      adding = true;
      if (window.LBF_CART) window.LBF_CART.setAdding(cta, true);
      if (cta) cta.disabled = true;

      track('lbf_bundle_add_to_cart_attempt', { dish_count: count, unique_dish_count: uniqueCount, bundle_value: subtotal });

      var items = buildLineItems();
      var ready = EDIT_BUNDLE_ID ? removeExistingBundle(EDIT_BUNDLE_ID) : Promise.resolve();
      ready.then(function () {
        return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
        });
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok) { throw new Error((res.body && res.body.description) || 'Add to cart failed'); }
          track('lbf_bundle_add_to_cart_success', { dish_count: count, unique_dish_count: uniqueCount, bundle_value: subtotal });
          return stampBundleCart().then(openCart);
        })
        .catch(function (err) {
          console.error('[lbf-bb] add failed', err);
          track('lbf_bundle_add_to_cart_error', {
            dish_count: count,
            unique_dish_count: uniqueCount,
            bundle_value: subtotal,
            error_message: err && err.message ? String(err.message).slice(0, 120) : 'unknown'
          });
          alert(lbnT('builder.add_error', 'Sorry, something went wrong adding your box. Please try again.') + '\n\n' + (err && err.message ? err.message : ''));
          adding = false;
          if (window.LBF_CART) window.LBF_CART.setAdding(cta, false);
          render();
        });
    }

    function stampBundleCart() {
      var attributes = {
        lbf_bundle_builder_used: 'yes',
        lbf_bundle_source_page: SOURCE_PAGE
      };
      if (analytics.context.starter_box) {
        attributes.lbf_starter_box = analytics.context.starter_box;
        attributes.lbf_starter_box_edited = analytics.context.starter_box_edited ? 'yes' : 'no';
      } else {
        /* Preserve the existing report definition: no starter-box value = self-built. */
        attributes.lbf_starter_box = '';
        attributes.lbf_starter_box_edited = '';
      }
      /* Carl 2026-10-13: the box label is written when the box is in the cart, never on a card tap, and it is
         added to any box already labelled (Shop All boxes) instead of replacing it. */
      var ctx = analytics.context, shark = ctx.offer && ctx.offer.kind === 'shark';
      var boxKey = shark ? ctx.offer.key : (ctx.starter_box_key && ctx.starter_box_key !== 'self-built' ? ctx.starter_box_key : '');
      var ready = !boxKey ? Promise.resolve() : fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .catch(function () { return null; })
        .then(function (cart) {
          var list = String((cart && cart.attributes && cart.attributes.lbf_box) || '').split(',').filter(function (v) { return v; });
          if (list.indexOf(boxKey) === -1) list.push(boxKey);
          attributes.lbf_box = list.join(',');
          attributes.lbf_box_source = 'rte-builder';
          attributes.lbf_box_edited = ctx.starter_box_edited ? 'yes' : 'no';
          attributes.lbf_box_substituted = !shark && ctx.substituted === 'yes' ? 'yes' : 'no';
        });
      return ready.then(function () { return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }); }).then(function (response) {
        if (!response.ok) throw new Error('Bundle attribution could not be saved');
      }).catch(function (error) {
        console.warn('[lbf-bb] cart attribution failed', error);
      });
    }

    function getBundleProperty(item, key) {
      var props = item && item.properties;
      if (!props) return '';
      if (!Array.isArray(props)) return props[key] || '';
      var found = props.find(function (pair) { return pair && (pair[0] === key || pair.name === key); });
      return found ? (found[1] || found.value || '') : '';
    }

    function removeExistingBundle(bundleId) {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var updates = {};
          (cart.items || []).forEach(function (item) {
            if (getBundleProperty(item, '_bundle_id') === bundleId) updates[item.key] = 0;
          });
          if (!Object.keys(updates).length) return null;
      return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates: updates })
          });
        });
    }

    function loadEditBundle() {
      if (!EDIT_BUNDLE_ID) return;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var found = false;
          dishes.forEach(function (dish) { dish.qtyByVariant = {}; });
          (cart.items || []).forEach(function (item) {
            if (getBundleProperty(item, '_bundle_id') !== EDIT_BUNDLE_ID) return;
            var variantId = Number(item.variant_id);
            var idx = dishes.findIndex(function (dish) {
              return dish.variants.some(function (variant) { return Number(variant.id) === variantId; });
            });
            if (idx !== -1) {
              dishes[idx].selected_variant_id = variantId;
              setVariantQty(dishes[idx], variantId, getVariantQty(dishes[idx], variantId) + (Number(item.quantity) || 0));
              found = true;
            }
          });
          if (found) {
            var openEditDrawer = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
            expanded = !!openEditDrawer;
            document.documentElement.classList.toggle('lbf-bb-edit-mode', openEditDrawer);
            document.body.classList.toggle('lbf-bb-edit-mode', openEditDrawer);
            syncExpanded();
            render();
          }
        })
        .catch(function (error) { console.warn('[lbf-bb] edit bundle load failed', error); });
    }

    function openCart() {
      expanded = false;
      syncExpanded();
      var confirmation;
      if (window.LBF_CART && window.LBF_DRAWER) {
        // Completion goes straight to the settled cart, without the Added sheet.
        confirmation = window.LBF_CART.load(true).then(function () { return window.LBF_DRAWER.open(); });
      } else {
        window.location.href = '/cart';
        confirmation = Promise.resolve();
      }
      return confirmation.finally(function () {
        adding = false;
        var cta = bar.querySelector('[data-bb-cta]');
        if (window.LBF_CART) window.LBF_CART.setAdding(cta, false);
        render();
      });
    }

    function renderDrawer(drawer) {
      fetch('/?sections=cart-drawer,cart-icon-bubble')
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          try {
            if (sections['cart-drawer']) {
              var tmp = document.createElement('div');
              tmp.innerHTML = sections['cart-drawer'];
              var fresh = tmp.querySelector('cart-drawer');
              var live = document.querySelector('cart-drawer');
              if (fresh && live) { live.innerHTML = fresh.innerHTML; live.classList.remove('is-empty'); }
            }
            if (sections['cart-icon-bubble']) {
              var bubble = document.getElementById('cart-icon-bubble');
              if (bubble) {
                var t2 = document.createElement('div');
                t2.innerHTML = sections['cart-icon-bubble'];
                var fb = t2.querySelector('#cart-icon-bubble') || t2.firstElementChild;
                if (fb) bubble.innerHTML = fb.innerHTML;
              }
            }
          } catch (e) {}
          if (typeof drawer.open === 'function') drawer.open();
          else window.location.href = '/cart';
        })
        .catch(function () { window.location.href = '/cart'; });
    }

    /* ---------- go ---------- */
    syncExpanded();
    render();
  }

  function boot() {
    document.querySelectorAll('[data-bb-root]').forEach(initRoot);
  }

  function normalizeQuickViewGallery(modal) {
    if (!modal) return;
    if (window.lbfHydrateHealthClaims) window.lbfHydrateHealthClaims(modal);
    // Product HTML upgrades the same media-gallery and slider-component used by PDP.
    // Keep their thumbnail, slide and accessibility behavior; never reset their scroll.
  }

  document.addEventListener('click', function (e) {
    var viewButton = e.target.closest('[data-bb-view], .bb-native-qv__open');
    if (viewButton) {
      var normalizeFromButton = function () {
        var opener = viewButton.closest('modal-opener');
        var modalId = viewButton.getAttribute('data-modal') ||
          (opener && opener.getAttribute('data-modal'));
        var modal = modalId ? document.querySelector(modalId) : document.querySelector('[id^="BBQuickView"][open], [id^="BBQuickView"].active');
        normalizeQuickViewGallery(modal);
      };
      setTimeout(normalizeFromButton, 120);
      setTimeout(normalizeFromButton, 360);
    }

    var tabButton = e.target.closest('[id^="BBQuickView"] .lbf-tabs__btn');
    if (!tabButton) return;
    var wrap = tabButton.closest('.lbf-tabs');
    if (!wrap) return;
    var idx = parseInt(tabButton.getAttribute('data-tab'), 10);
    var btns = wrap.querySelectorAll('.lbf-tabs__btn');
    var panels = wrap.querySelectorAll('.lbf-tabs__panel');
    btns.forEach(function (button) {
      button.classList.remove('lbf-tabs__btn--active');
      button.setAttribute('aria-selected', 'false');
    });
    panels.forEach(function (panel) { panel.classList.remove('lbf-tabs__panel--active'); });
    if (btns[idx]) {
      btns[idx].classList.add('lbf-tabs__btn--active');
      btns[idx].setAttribute('aria-selected', 'true');
    }
    if (panels[idx]) panels[idx].classList.add('lbf-tabs__panel--active');
  });

  document.addEventListener('wheel', function (e) {
    var list = e.target.closest('[id^="BBQuickView"] .thumbnail-list');
    if (!list || list.scrollWidth <= list.clientWidth) return;
    var delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    if (!delta) return;
    var before = list.scrollLeft;
    list.scrollLeft += delta;
    if (list.scrollLeft !== before) e.preventDefault();
  }, { passive:false });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
